import { landingCopy } from '../copy';
import { buildLandingPathForMarket, type LandingMarket } from '../regional';

type LandingLocale = keyof typeof landingCopy;
// Union across locales so any locale's (structurally identical) platform copy is
// accepted — locales differ only in string literals, not shape.
type PlatformCopy = (typeof landingCopy)[LandingLocale]['platform'];

export type V2Link = { label: string; href: string };

export type V2Hero = {
  badge: string;
  titleLines: readonly string[];
  accentIndex: number;
  body: string;
  primaryCta: V2Link;
  secondaryCta: V2Link;
  microProof: readonly string[];
  screenImage: string;
  screenAlt: string;
  floatCards: {
    booking: { label: string; title: string; meta: string };
    revenue: { label: string; value: string; meta: string };
    scribe: { label: string; body: string };
  };
};

export type V2Shift = {
  eyebrow: string;
  title: string;
  body: string;
  oldTitle: string;
  oldSubtitle: string;
  oldItems: readonly string[];
  newTitle: string;
  newSubtitle: string;
  newItems: readonly string[];
};

export type V2Pillar = {
  number: string;
  eyebrow: string;
  title: string;
  body: string;
  items: readonly string[];
  image: string;
  imageAlt: string;
  link: V2Link;
  chip: string;
};

export type V2Pillars = {
  eyebrow: string;
  title: string;
  accent: string;
  body: string;
  pillars: readonly V2Pillar[];
};

export type V2Scribe = {
  eyebrow: string;
  title: string;
  accent: string;
  body: string;
  cta: V2Link;
  listening: string;
  noteLines: readonly string[];
  noteHeading: string;
};

export type V2Proof = {
  eyebrow: string;
  title: string;
  source: string;
  stats: PlatformCopy['roiStats'];
};

export type V2Founding = {
  eyebrow: string;
  title: string;
  body: string;
  points: readonly string[];
  cta: V2Link;
  stampTop: string;
  stampBottom: string;
};

export type V2Capabilities = {
  eyebrow: string;
  title: string;
  accent: string;
  groups: PlatformCopy['capabilityGroups'];
  images: readonly string[];
};

export type V2Finale = {
  titleLine1: string;
  titleLine2: string;
  body: string;
  specialtiesLabel: string;
  specialties: readonly string[];
  ctaTitle: string;
  ctaBody: string;
  primaryCta: V2Link;
  secondaryCta: V2Link;
};

export type LandingV2Content = {
  hero: V2Hero;
  marquee: readonly string[];
  shift: V2Shift;
  pillars: V2Pillars;
  scribe: V2Scribe;
  proof: V2Proof;
  founding: V2Founding;
  capabilities: V2Capabilities;
  finale: V2Finale;
};

const MICRO: Record<
  LandingLocale,
  {
    badge: string;
    secondary: string;
    heroProof: readonly string[];
    bookingLabel: string;
    bookingTitle: string;
    bookingMeta: string;
    revenueLabel: string;
    revenueValue: string;
    revenueMeta: string;
    scribeLabel: string;
    scribeBody: string;
    shiftEyebrow: string;
    pillarsEyebrow: string;
    pillarsTitle: string;
    pillarsAccent: string;
    scribeEyebrow: string;
    scribeAccent: string;
    noteHeading: string;
    noteLines: readonly string[];
    proofEyebrow: string;
    stampTop: string;
    stampBottom: string;
    capabilitiesEyebrow: string;
    specialtiesLabel: string;
  }
> = {
  en: {
    badge: 'The operating system for modern clinics',
    secondary: 'Explore the platform',
    heroProof: ['Scheduling to payments in one flow', 'AI notes inside the visit', 'Built with founding clinics'],
    bookingLabel: 'New booking',
    bookingTitle: 'Sofia M. — Consultation',
    bookingMeta: 'Tomorrow · 9:30 AM · Confirmed',
    revenueLabel: 'This month',
    revenueValue: '+18%',
    revenueMeta: 'Rebooked revenue',
    scribeLabel: 'AI Scribe',
    scribeBody: 'Note drafted. Review and sign.',
    shiftEyebrow: 'Why clinics switch',
    pillarsEyebrow: 'The platform',
    pillarsTitle: 'Everything your clinic runs on,',
    pillarsAccent: 'finally in rhythm.',
    scribeEyebrow: 'Elen1 AI Scribe',
    scribeAccent: 'while you stay with the patient',
    noteHeading: 'Consult note — drafting',
    noteLines: [
      'Chief complaint: follow-up, skin assessment',
      'Assessment: improvement since last visit',
      'Plan: continue regimen, review in 4 weeks',
      'Follow-up reminder scheduled automatically',
    ],
    proofEyebrow: 'The return',
    stampTop: 'Founding clinic',
    stampBottom: 'Limited seats',
    capabilitiesEyebrow: 'The full toolkit',
    specialtiesLabel: 'Built for',
  },
  es: {
    badge: 'El sistema operativo para clínicas modernas',
    secondary: 'Explorar la plataforma',
    heroProof: ['De agenda a cobros en un solo flujo', 'Notas con IA dentro de la consulta', 'Creado con clínicas fundadoras'],
    bookingLabel: 'Nueva reserva',
    bookingTitle: 'Sofía M. — Consulta',
    bookingMeta: 'Mañana · 9:30 AM · Confirmada',
    revenueLabel: 'Este mes',
    revenueValue: '+18%',
    revenueMeta: 'Ingresos por recitas',
    scribeLabel: 'IA Scribe',
    scribeBody: 'Nota redactada. Revisa y firma.',
    shiftEyebrow: 'Por qué las clínicas cambian',
    pillarsEyebrow: 'La plataforma',
    pillarsTitle: 'Todo lo que mueve tu clínica,',
    pillarsAccent: 'por fin en ritmo.',
    scribeEyebrow: 'Elen1 IA Scribe',
    scribeAccent: 'mientras tú sigues con el paciente',
    noteHeading: 'Nota de consulta — redactando',
    noteLines: [
      'Motivo: seguimiento, evaluación de piel',
      'Evaluación: mejoría desde la última visita',
      'Plan: continuar el tratamiento, revisar en 4 semanas',
      'Recordatorio de seguimiento programado automáticamente',
    ],
    proofEyebrow: 'El retorno',
    stampTop: 'Clínica fundadora',
    stampBottom: 'Cupos limitados',
    capabilitiesEyebrow: 'Todas las herramientas',
    specialtiesLabel: 'Hecho para',
  },
  it: {
    badge: 'Il sistema operativo per cliniche moderne',
    secondary: 'Scopri la piattaforma',
    heroProof: ['Da agenda a incassi in un unico flusso', 'Note con IA durante la visita', 'Creato con le cliniche fondatrici'],
    bookingLabel: 'Nuova prenotazione',
    bookingTitle: 'Sofia M. — Consulto',
    bookingMeta: 'Domani · 9:30 · Confermata',
    revenueLabel: 'Questo mese',
    revenueValue: '+18%',
    revenueMeta: 'Ricavi da riprenotazioni',
    scribeLabel: 'AI Scribe',
    scribeBody: 'Nota pronta. Rivedi e firma.',
    shiftEyebrow: 'Perché le cliniche cambiano',
    pillarsEyebrow: 'La piattaforma',
    pillarsTitle: 'Tutto ciò che muove la tua clinica,',
    pillarsAccent: 'finalmente in ritmo.',
    scribeEyebrow: 'Elen1 AI Scribe',
    scribeAccent: 'mentre tu resti con il paziente',
    noteHeading: 'Nota di consulto — in scrittura',
    noteLines: [
      'Motivo: follow-up, valutazione della pelle',
      'Valutazione: miglioramento dall’ultima visita',
      'Piano: continuare il trattamento, controllo tra 4 settimane',
      'Promemoria di follow-up programmato automaticamente',
    ],
    proofEyebrow: 'Il ritorno',
    stampTop: 'Clinica fondatrice',
    stampBottom: 'Posti limitati',
    capabilitiesEyebrow: 'Tutti gli strumenti',
    specialtiesLabel: 'Pensato per',
  },
};

export function buildLandingV2Content(copy: PlatformCopy, locale: LandingLocale, market: LandingMarket): LandingV2Content {
  const micro = MICRO[locale];
  const demoHref = buildLandingPathForMarket('/book-demo', market);
  const featuresHref = buildLandingPathForMarket('/features', market);

  return {
    hero: {
      badge: micro.badge,
      titleLines: [copy.heroLine1, copy.heroHighlight, copy.heroLine3],
      accentIndex: 1,
      body: copy.heroSubtitle,
      primaryCta: { label: copy.heroCta, href: demoHref },
      secondaryCta: { label: micro.secondary, href: featuresHref },
      microProof: micro.heroProof,
      screenImage: '/landing-captures/dashboard-standalone.png',
      screenAlt: copy.heroImageAlt,
      floatCards: {
        booking: { label: micro.bookingLabel, title: micro.bookingTitle, meta: micro.bookingMeta },
        revenue: { label: micro.revenueLabel, value: micro.revenueValue, meta: micro.revenueMeta },
        scribe: { label: micro.scribeLabel, body: micro.scribeBody },
      },
    },
    marquee: copy.practiceTypes,
    shift: {
      eyebrow: micro.shiftEyebrow,
      title: copy.comparisonTitle,
      body: copy.twinBody,
      oldTitle: copy.comparisonOldTitle,
      oldSubtitle: copy.comparisonOldSubtitle,
      oldItems: copy.comparisonOldItems,
      newTitle: copy.comparisonNewTitle,
      newSubtitle: copy.comparisonNewSubtitle,
      newItems: copy.comparisonNewItems,
    },
    pillars: {
      eyebrow: micro.pillarsEyebrow,
      title: micro.pillarsTitle,
      accent: micro.pillarsAccent,
      body: copy.doctorsBody,
      pillars: [
        {
          number: '01',
          eyebrow: copy.schedulingSectionEyebrow,
          title: copy.schedulingSectionTitle,
          body: copy.schedulingSectionBody,
          items: copy.schedulingSectionItems,
          image: '/landing-captures/scheduling-standalone.png',
          imageAlt: copy.schedulingSectionImageAlt,
          link: { label: copy.schedulingSectionCta, href: buildLandingPathForMarket('/calendar-booking', market) },
          chip: copy.schedulingSectionItems[3],
        },
        {
          number: '02',
          eyebrow: copy.operationsSectionEyebrow,
          title: copy.operationsSectionTitle,
          body: copy.operationsSectionBody,
          items: copy.operationsSectionItems,
          image: '/landing-captures/staff-operations-standalone.png',
          imageAlt: copy.operationsSectionImageAlt,
          link: { label: copy.operationsSectionCta, href: buildLandingPathForMarket('/multi-site-operations', market) },
          chip: copy.operationsSectionItems[1],
        },
        {
          number: '03',
          eyebrow: copy.growthSectionEyebrow,
          title: copy.growthSectionTitle,
          body: copy.growthSectionBody,
          items: copy.growthSectionItems,
          image: '/landing-captures/reports-standalone.png',
          imageAlt: copy.growthSectionImageAlt,
          link: { label: copy.growthSectionCta, href: buildLandingPathForMarket('/marketing-growth', market) },
          chip: copy.growthSectionItems[0],
        },
      ],
    },
    scribe: {
      eyebrow: micro.scribeEyebrow,
      title: `${copy.aiScribeTitlePrefix} ${copy.aiScribeTitleSuffix}`,
      accent: micro.scribeAccent,
      body: copy.aiScribeCardBody,
      cta: { label: copy.aiScribeCta, href: buildLandingPathForMarket('/clinical-ai', market) },
      listening: copy.aiScribeListening,
      noteHeading: micro.noteHeading,
      noteLines: micro.noteLines,
    },
    proof: {
      eyebrow: micro.proofEyebrow,
      title: copy.roiTitle,
      source: copy.roiSource,
      stats: copy.roiStats,
    },
    founding: {
      eyebrow: copy.customerStoriesEyebrow,
      title: copy.customerStoriesTitle,
      body: copy.customerStoriesBody,
      points: copy.customerStoriesPoints,
      cta: { label: copy.customerStoriesCta, href: demoHref },
      stampTop: micro.stampTop,
      stampBottom: micro.stampBottom,
    },
    capabilities: {
      eyebrow: micro.capabilitiesEyebrow,
      title: copy.capabilitiesTitlePrefix,
      accent: copy.capabilitiesTitleHighlight,
      groups: copy.capabilityGroups,
      images: [
        '/landing-captures/scheduling-standalone.png',
        '/landing-captures/patients-standalone.png',
        '/landing-captures/billing-standalone.png',
        '/landing-captures/marketing-standalone.png',
      ],
    },
    finale: {
      titleLine1: copy.twinTitleLine1,
      titleLine2: copy.twinTitleLine2,
      body: copy.twinBody,
      specialtiesLabel: MICRO[locale].specialtiesLabel,
      specialties: copy.practiceTypes,
      ctaTitle: copy.contactTitle,
      ctaBody: copy.contactBody,
      primaryCta: { label: copy.heroCta, href: demoHref },
      secondaryCta: { label: micro.secondary, href: featuresHref },
    },
  };
}
